// ---------------------------------------------------------------------------
// DnD Email Editor V2 – Main Orchestrator
// ---------------------------------------------------------------------------

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Undo2, Redo2, History, LayoutTemplate, Monitor, Smartphone, FileText, Eye, PanelLeftOpen } from 'lucide-react';
import { useDndEditor } from './useDndEditor';
import { DndToolbar } from './DndToolbar';
import { DndBlockPalette } from './DndBlockPalette';
import { DndCanvas, type InlineEditRequest } from './DndCanvas';
import { DndInspector } from './DndInspector';
import { DndGlobalStyles } from './DndGlobalStyles';
import { DndHtmlCodeModal } from './DndHtmlCodeModal';
import { DndColumnsModal } from './DndColumnsModal';
import { DndSocialModal, type SocialModalResult } from './DndSocialModal';
import { ColorPresetsContext, type PresetColor } from './dnd-color-presets';
import { useColorPalette } from '@/hooks/useColorPalette';
import { DesktopOnlyNotice, useDesktopGate } from '@/components/layout/RequireDesktop';
import {
  getBlockById,
  getBlockByType,
  isComponentRef,
  type BlockTier,
  type SavedBlockRef,
  type SavedBlockEntry,
} from './dnd-blocks';
import { DndComponentPicker } from './DndComponentPicker';
import {
  convertLayerToElement,
  convertElementToLayer,
  logosFromSet,
  createColumnsLayer,
  createSocialLayer,
  createSocialElement,
} from './dnd-defaults';
import { useSocialLogoSets } from '@/hooks/useSocialLogoSets';
import { useToast } from '@/hooks/useToast';
import { cloneLayersWithNewIds } from './dnd-model';
import { FtpPhotoPicker } from '@/components/ftp-editor/FtpPhotoPicker';
import { FtpPreviewModal } from '@/components/ftp-editor/FtpPreviewModal';
import { FtpLinkModal } from '@/components/ftp-editor/FtpLinkModal';
import { FtpVariableInserter } from '@/components/ftp-editor/FtpVariableInserter';
import { FtpTemplatePicker, type FtpTemplateRow } from '@/components/ftp-editor/FtpTemplatePicker';
import { DEFAULT_EDITOR_VARIABLES } from '@/components/ftp-editor/ftp-editor-utils';
import type { DndEmailModel, LayerType, ElementType, ColumnsLayer, ColumnsLayout, Layer, SocialLink } from './dnd-model';
import { compileModel } from './dnd-compiler';
import { compileToPlainText } from './dnd-plain-text';
import { migrateModel, parseHtmlToModel } from './dnd-parser';
import { isDndHtml } from './dnd-marker';
import './dnd-editor.css';

/**
 * Whether a template was authored in this editor, and so can be loaded back
 * into it. The template *list* endpoint returns html but not json_content, so
 * this has to key off the marker the compiler stamps on every DnD email; the
 * json check only pays off for the one template the picker has resolved /full.
 */
function isDndTemplate(tmpl: FtpTemplateRow): boolean {
  const raw = tmpl.json_content?.trim();
  if (raw && raw !== 'null' && raw.includes('"layers"')) return true;
  return isDndHtml(tmpl.html_content);
}

/**
 * The DnD model behind a saved template, or null when the template is plain
 * HTML and so can't be loaded into this editor. Prefers the stored json_content
 * and falls back to re-parsing html compiled by this editor (templates saved
 * before json_content was persisted still carry data-ftp-editor-mode="dnd").
 */
function modelFromTemplate(tmpl: FtpTemplateRow): DndEmailModel | null {
  const raw = tmpl.json_content?.trim();
  if (raw && raw !== 'null') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { layers?: unknown }).layers)) {
        return migrateModel(parsed as Record<string, unknown>);
      }
    } catch {
      // fall through to the html parse
    }
  }
  return parseHtmlToModel(tmpl.html_content || '');
}

// ---- Public API -----------------------------------------------------------

export interface DndEditorProps {
  initialModel?: DndEmailModel;
  onSave?: (model: DndEmailModel, html: string, plainText: string) => void;
  onChange?: () => void;
  onConvertToHtml?: (html: string) => void;
  initialFullscreen?: boolean;
  onClose?: () => void;
  /** Merge-tag tokens for the "Insert Variable" picker. Defaults to the shared set. */
  variables?: Record<string, string>;
  /**
   * The saved-block library, in palette order: the account default header and
   * footer plus any Email Components the account has built. The "Saved" palette
   * category is rendered straight from this array, so only what is passed shows
   * up. Inserting one drops fresh-id clones of its layers.
   */
  savedBlocks?: SavedBlockEntry[];
  /**
   * Slim preview layout: canvas only (no palette/inspector), with a thin toolbar
   * whose "Open Editor" button expands to the full editor in fullscreen. Text and
   * image blocks stay inline-editable in the canvas; everything else needs the
   * full editor. Mirrors FtpEditor's previewMode.
   */
  previewMode?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  /** When provided, the toolbar's Save becomes a menu with "Save as New Version". */
  onSaveAsNewVersion?: (model: DndEmailModel, html: string, plainText: string) => void;
  /** Hide the built-in Save button when the host page provides its own Save. */
  hideSave?: boolean;
  /**
   * Show the "Choose template" button. Off by default because this editor also
   * edits email *fragments* (the marketing-settings header / footer), where
   * loading a whole email template makes no sense. Only DnD-authored templates
   * can be applied; plain-HTML ones are blocked in the picker.
   */
  enableTemplatePicker?: boolean;
  /** Fires after a template is applied, so the host page can adopt its subject / preheader / from. */
  onTemplateApply?: (template: FtpTemplateRow) => void;
}

export interface DndEditorHandle {
  getModel: () => DndEmailModel;
  getHtml: () => string;
  getPlainText: () => string;
  isDirty: () => boolean;
  markSaved: () => void;
  openFullscreen: () => void;
  openTemplatePicker: () => void;
}

// ---- Component ------------------------------------------------------------

const DndEditor = forwardRef<DndEditorHandle, DndEditorProps>(
  function DndEditor(
    { initialModel, onSave, onSaveAsNewVersion, onChange, onConvertToHtml, initialFullscreen = false, onClose, variables: variablesProp, savedBlocks, previewMode = false, onFullscreenChange, hideSave = false, enableTemplatePicker = false, onTemplateApply },
    ref,
  ) {
    const editor = useDndEditor(initialModel);
    const toast = useToast();
    const [rightTab, setRightTab] = useState<'inspector' | 'global'>('inspector');
    const [device, setDevice] = useState<'desktop' | 'mobile' | 'plaintext'>('desktop');
    const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);

    // ---- Plain text state ----
    const [plainTextContent, setPlainTextContent] = useState('');
    const isPlainText = device === 'plaintext';

    // ---- Preview modal ----
    const [previewOpen, setPreviewOpen] = useState(false);

    // ---- Template picker ----
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

    // ---- Inline-edit requests (inspector "Edit Text" → canvas WYSIWYG) ----
    // Inline editing lives in the canvas; the inspector is a sibling panel, so a
    // request is passed down and cleared once the canvas has acted on it.
    const [editRequest, setEditRequest] = useState<InlineEditRequest | null>(null);

    const requestInlineEdit = useCallback((layerId: string, columnId?: string, elementId?: string) => {
      setEditRequest({ layerId, columnId, elementId });
    }, []);

    const handleEditRequestHandled = useCallback(() => setEditRequest(null), []);

    // ---- Photo picker (callback-ref pattern from FtpEditor) ----
    const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
    const photoPickerCallback = useRef<((url: string, alt: string) => void) | null>(null);

    const handleOpenPhotoPicker = useCallback((cb: (url: string, alt: string) => void) => {
      photoPickerCallback.current = cb;
      setPhotoPickerOpen(true);
    }, []);

    const handlePhotoSelect = useCallback((url: string, alt: string) => {
      if (photoPickerCallback.current) {
        photoPickerCallback.current(url, alt);
        photoPickerCallback.current = null;
      }
      setPhotoPickerOpen(false);
    }, []);

    // ---- Link + Variable modals (callback-ref pattern, shared with FtpEditor) ----
    const variables = variablesProp && Object.keys(variablesProp).length > 0 ? variablesProp : DEFAULT_EDITOR_VARIABLES;

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalHasSelection, setLinkModalHasSelection] = useState(false);
    const linkCallback = useRef<((url: string, text: string) => void) | null>(null);

    const [variableModalOpen, setVariableModalOpen] = useState(false);
    const variableCallback = useRef<((token: string) => void) | null>(null);

    const handleOpenLinkModal = useCallback((hasSelection: boolean, cb: (url: string, text: string) => void) => {
      linkCallback.current = cb;
      setLinkModalHasSelection(hasSelection);
      setLinkModalOpen(true);
    }, []);

    const handleLinkInsert = useCallback((url: string, text: string) => {
      if (linkCallback.current) {
        linkCallback.current(url, text);
        linkCallback.current = null;
      }
      setLinkModalOpen(false);
    }, []);

    const handleOpenVariableModal = useCallback((cb: (token: string) => void) => {
      variableCallback.current = cb;
      setVariableModalOpen(true);
    }, []);

    const handleVariableSelect = useCallback((token: string) => {
      if (variableCallback.current) {
        variableCallback.current(token);
        variableCallback.current = null;
      }
      setVariableModalOpen(false);
    }, []);

    // ---- HTML block code modal (callback-ref pattern) ----
    const [htmlModalOpen, setHtmlModalOpen] = useState(false);
    const [htmlModalInitial, setHtmlModalInitial] = useState('');
    const htmlModalCallback = useRef<((html: string) => void) | null>(null);

    const handleOpenHtmlModal = useCallback((initialHtml: string, cb: (html: string) => void) => {
      htmlModalCallback.current = cb;
      setHtmlModalInitial(initialHtml);
      setHtmlModalOpen(true);
    }, []);

    const handleHtmlModalSave = useCallback((html: string) => {
      if (htmlModalCallback.current) {
        htmlModalCallback.current(html);
        htmlModalCallback.current = null;
      }
      setHtmlModalOpen(false);
    }, []);

    // ---- Columns layout modal (callback-ref pattern) ----
    // Prompts for the column layout when a columns block is first added, and
    // reused (seeded with the current layout) to edit an existing columns layer.
    const [columnsModalOpen, setColumnsModalOpen] = useState(false);
    const [columnsModalInitialLayout, setColumnsModalInitialLayout] = useState<ColumnsLayout>('2');
    const [columnsModalConfirmLabel, setColumnsModalConfirmLabel] = useState('Add Columns');
    const columnsModalCallback = useRef<((layout: ColumnsLayout) => void) | null>(null);

    const handleOpenColumnsModal = useCallback((cb: (layout: ColumnsLayout) => void, initialLayout: ColumnsLayout = '2', confirmLabel = 'Add Columns') => {
      columnsModalCallback.current = cb;
      setColumnsModalInitialLayout(initialLayout);
      setColumnsModalConfirmLabel(confirmLabel);
      setColumnsModalOpen(true);
    }, []);

    // Open the modal to change an existing columns layer's layout.
    const handleOpenColumnsModalForEdit = useCallback((currentLayout: ColumnsLayout, cb: (layout: ColumnsLayout) => void) => {
      handleOpenColumnsModal(cb, currentLayout, 'Apply Layout');
    }, [handleOpenColumnsModal]);

    const handleColumnsModalConfirm = useCallback((layout: ColumnsLayout) => {
      if (columnsModalCallback.current) {
        columnsModalCallback.current(layout);
        columnsModalCallback.current = null;
      }
      setColumnsModalOpen(false);
    }, []);

    // ---- Social links modal (callback-ref pattern) ----
    // Opened when a social block is added and on double-click of a social layer/
    // element; edits only the links (platforms + URLs).
    const [socialModalOpen, setSocialModalOpen] = useState(false);
    const [socialModalLinks, setSocialModalLinks] = useState<SocialLink[]>([]);
    const [socialModalLogoSetId, setSocialModalLogoSetId] = useState<number | null>(null);
    const [socialModalPreviewBg, setSocialModalPreviewBg] = useState('#ffffff');
    const [socialModalConfirmLabel, setSocialModalConfirmLabel] = useState('Apply');
    const socialModalCallback = useRef<((result: SocialModalResult) => void) | null>(null);

    const handleOpenSocialModal = useCallback((links: SocialLink[], logoSetId: number | null, previewBg: string, cb: (result: SocialModalResult) => void, confirmLabel = 'Apply') => {
      socialModalCallback.current = cb;
      setSocialModalLinks(links);
      setSocialModalLogoSetId(logoSetId);
      setSocialModalPreviewBg(previewBg);
      setSocialModalConfirmLabel(confirmLabel);
      setSocialModalOpen(true);
    }, []);

    const handleSocialModalSave = useCallback((result: SocialModalResult) => {
      if (socialModalCallback.current) {
        socialModalCallback.current(result);
        socialModalCallback.current = null;
      }
      setSocialModalOpen(false);
    }, []);

    // Master-account brand palette → Primary/Standard swatch rows in the pickers.
    const { data: paletteColors } = useColorPalette();
    const colorPresets = useMemo(() => {
      const toPreset = (type: 'primary' | 'standard'): PresetColor[] =>
        (paletteColors ?? [])
          .filter((c) => c.active && c.colorType === type)
          .map((c) => ({ value: c.colorHex, label: c.name || c.colorHex }));
      return { primary: toPreset('primary'), standard: toPreset('standard') };
    }, [paletteColors]);

    // ---- Columns layer helper (for click-to-add) ----
    const selectedColumnsLayer = useMemo(() => {
      if (!editor.selection.layerId) return null;
      const layer = editor.model.layers.find((l) => l.id === editor.selection.layerId);
      return layer?.type === 'columns' ? (layer as ColumnsLayer) : null;
    }, [editor.model.layers, editor.selection.layerId]);

    // ---- Drag state ----
    const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
    const [draggedBlockTier, setDraggedBlockTier] = useState<BlockTier | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
    const [columnDropTarget, setColumnDropTarget] = useState<{ layerId: string; columnId: string } | null>(null);

    const isDragging = draggedBlockId !== null;

    // ---- Layer reorder drag state ----
    const [reorderFromIndex, setReorderFromIndex] = useState<number | null>(null);
    const isReordering = reorderFromIndex !== null;

    // ---- Element move drag state (dragging a placed block) ----
    type ElementDragPoint = { layerId: string; columnId: string; index: number };
    const [elementDragSource, setElementDragSource] = useState<ElementDragPoint | null>(null);
    const [elementDropTarget, setElementDropTarget] = useState<ElementDragPoint | null>(null);
    const isElementDragging = elementDragSource !== null;

    const handleReorderStart = useCallback((fromIndex: number) => {
      // A layer reorder starts — make sure no element drag is mid-flight.
      setElementDragSource(null);
      setElementDropTarget(null);
      setReorderFromIndex(fromIndex);
      const layer = editor.model.layers[fromIndex];
      if (layer) {
        const block = getBlockByType(layer.type);
        setDraggedBlockTier(block?.tier ?? 'layer');
      }
    }, [editor.model.layers]);

    const handleReorderEnd = useCallback(() => {
      setReorderFromIndex(null);
      setDraggedBlockTier(null);
      setDropTargetIndex(null);
      setColumnDropTarget(null);
      setElementDragSource(null);
      setElementDropTarget(null);
    }, []);

    const handleReorderDrop = useCallback((toIndex: number) => {
      if (reorderFromIndex === null) return;

      // Dropped into a column cell — convert layer to element
      if (columnDropTarget) {
        const layer = editor.model.layers[reorderFromIndex];
        if (layer) {
          const element = convertLayerToElement(layer);
          if (element) {
            editor.removeLayerById(layer.id);
            editor.addElementInstance(
              columnDropTarget.layerId,
              columnDropTarget.columnId,
              element,
            );
          }
        }
      } else if (toIndex !== reorderFromIndex && toIndex !== reorderFromIndex + 1) {
        const adjustedTo = toIndex > reorderFromIndex ? toIndex - 1 : toIndex;
        editor.moveLayerByIndex(reorderFromIndex, adjustedTo);
      }

      setReorderFromIndex(null);
      setDraggedBlockTier(null);
      setDropTargetIndex(null);
      setColumnDropTarget(null);
    }, [reorderFromIndex, columnDropTarget, editor]);

    // ---- Element move handlers ----

    const handleElementDragStart = useCallback((layerId: string, columnId: string, index: number) => {
      // Starting an element move — clear the palette-drop and layer-reorder sources
      // so exactly one drag source is ever active.
      setDraggedBlockId(null);
      setDraggedBlockTier(null);
      setDropTargetIndex(null);
      setColumnDropTarget(null);
      setReorderFromIndex(null);
      setElementDropTarget(null);
      setElementDragSource({ layerId, columnId, index });
    }, []);

    const handleElementDragEnd = useCallback(() => {
      setElementDragSource(null);
      setElementDropTarget(null);
      setDropTargetIndex(null);
    }, []);

    // Drop into a column cell: reorder within a column or move between columns.
    const handleElementDrop = useCallback(() => {
      const src = elementDragSource;
      const tgt = elementDropTarget;
      if (src && tgt) {
        const srcLayer = editor.model.layers.find((l) => l.id === src.layerId);
        const srcCol = srcLayer && srcLayer.type === 'columns'
          ? (srcLayer as ColumnsLayer).columns.find((c) => c.id === src.columnId)
          : undefined;
        const moved = srcCol?.elements[src.index];
        if (moved) {
          if (src.layerId === tgt.layerId) {
            let toIdx = tgt.index;
            if (src.columnId === tgt.columnId && tgt.index > src.index) toIdx = tgt.index - 1;
            editor.moveElementByIndex(src.layerId, src.columnId, src.index, tgt.columnId, toIdx);
          } else {
            // Cross-layer move: pull the element out and re-insert into the target.
            editor.removeElementById(src.layerId, src.columnId, moved.id);
            editor.addElementInstance(tgt.layerId, tgt.columnId, moved, tgt.index);
          }
          // Keep the moved block selected in its new home (selection.columnId
          // would otherwise be stale after a cross-column move).
          editor.selectElement(tgt.layerId, tgt.columnId, moved.id);
        }
      }
      setElementDragSource(null);
      setElementDropTarget(null);
      setDropTargetIndex(null);
    }, [elementDragSource, elementDropTarget, editor]);

    // Drop on a layer gap: convert the dragged element into a top-level layer.
    const handleElementDragOut = useCallback((toIndex: number) => {
      const src = elementDragSource;
      if (src) {
        const srcLayer = editor.model.layers.find((l) => l.id === src.layerId);
        if (srcLayer && srcLayer.type === 'columns') {
          const col = (srcLayer as ColumnsLayer).columns.find((c) => c.id === src.columnId);
          const moved = col?.elements[src.index];
          if (moved) {
            const newLayer = convertElementToLayer(moved);
            if (newLayer) {
              editor.removeElementById(src.layerId, src.columnId, moved.id);
              editor.addLayerInstance(newLayer, toIndex);
            }
          }
        }
      }
      setElementDragSource(null);
      setElementDropTarget(null);
      setDropTargetIndex(null);
    }, [elementDragSource, editor]);

    // Notify on every model mutation, not only on the false→true flip of
    // isDirty. Firing once on the transition means a consumer that ignores that
    // one call — the campaign editor suppresses changes for a beat after mount,
    // and the social-logo backfill below dirties the model inside that window —
    // never hears about any later edit, because isDirty stays true from then on.
    // Kept in a ref so a parent re-creating its handler doesn't re-fire.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(() => {
      if (editor.isDirty) onChangeRef.current?.();
    }, [editor.model, editor.isDirty]);

    // Built-in social icons are inline SVG (Gmail strips them, Outlook can't
    // render them), so every social block needs a hosted logo set for the sent
    // email to show icons. The inspector auto-picks one only when a block is
    // selected; do it here too so blocks that were never inspected (e.g. added
    // then saved from the preview pane) still send with email-safe icons.
    const { data: logoSets } = useSocialLogoSets();
    useEffect(() => {
      if (!logoSets?.length) return;
      const firstSet = logoSets[0];
      const logos = logosFromSet(firstSet);
      const isValid = (id?: number | null) => id != null && logoSets.some((s) => s.socialLogoSetID === id);
      for (const layer of editor.model.layers) {
        if (layer.type === 'social' && !isValid(layer.logoSetId)) {
          editor.updateLayerById(layer.id, { logoSetId: firstSet.socialLogoSetID, logos } as Partial<Layer>);
        } else if (layer.type === 'columns') {
          for (const col of (layer as ColumnsLayer).columns) {
            for (const el of col.elements) {
              if (el.type === 'social' && !isValid(el.logoSetId)) {
                editor.updateElementById(layer.id, col.id, el.id, { logoSetId: firstSet.socialLogoSetID, logos } as Partial<import('./dnd-model').Element>);
              }
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoSets, editor.model.layers]);

    // Notify the parent when the editor enters/leaves fullscreen (skip mount).
    const onFullscreenChangeRef = useRef(onFullscreenChange);
    onFullscreenChangeRef.current = onFullscreenChange;
    const fsMountedRef = useRef(false);
    useEffect(() => {
      if (!fsMountedRef.current) { fsMountedRef.current = true; return; }
      onFullscreenChangeRef.current?.(isFullscreen);
    }, [isFullscreen]);

    useImperativeHandle(ref, () => ({
      getModel: () => editor.model,
      getHtml: () => compileModel(editor.model),
      getPlainText: () => {
        if (plainTextContent) return plainTextContent;
        return compileToPlainText(editor.model);
      },
      isDirty: () => editor.isDirty,
      markSaved: () => editor.markSaved(),
      openFullscreen: () => setIsFullscreen(true),
      openTemplatePicker: () => setTemplatePickerOpen(true),
    }));

    // ---- Palette drag ----

    const handlePaletteDragStart = useCallback((blockId: string, tier: BlockTier) => {
      // A palette drag starts — clear any in-flight element move.
      setElementDragSource(null);
      setElementDropTarget(null);
      setDraggedBlockId(blockId);
      setDraggedBlockTier(tier);
    }, []);

    const handleDragEnd = useCallback(() => {
      setDraggedBlockId(null);
      setDraggedBlockTier(null);
      setDropTargetIndex(null);
      setColumnDropTarget(null);
    }, []);

    // ---- Saved blocks (account default header / footer + Email Components) ----

    // Only entries that actually carry layers are offered.
    const savedEntries = useMemo<SavedBlockEntry[]>(
      () => (savedBlocks ?? []).filter((e) => e.layers?.length),
      [savedBlocks],
    );

    // The component library, separate from the header/footer defaults: it sits
    // behind the picker rather than getting a palette tile each.
    const componentEntries = useMemo(
      () => savedEntries.filter((e) => isComponentRef(e.ref)),
      [savedEntries],
    );

    // Insert fresh-id clones of a saved block's layers at the given index.
    const insertSavedBlock = useCallback((ref: SavedBlockRef, index?: number) => {
      const source = savedEntries.find((e) => e.ref === ref)?.layers;
      if (!source || source.length === 0) return;
      editor.addLayersInstance(cloneLayersWithNewIds(source), index);
    }, [savedEntries, editor]);

    // Where a component chosen in the picker should land. Captured when the
    // picker is opened (the drop position, or the click-to-add position) because
    // by the time the user confirms, the drag state has been cleared.
    const [componentPickerOpen, setComponentPickerOpen] = useState(false);
    const pendingComponentIndex = useRef<number | undefined>(undefined);

    const openComponentPicker = useCallback((index?: number) => {
      pendingComponentIndex.current = index;
      setComponentPickerOpen(true);
    }, []);

    const handleComponentPicked = useCallback((ref: SavedBlockRef) => {
      insertSavedBlock(ref, pendingComponentIndex.current);
      pendingComponentIndex.current = undefined;
    }, [insertSavedBlock]);

    // ---- Canvas drop ----

    const openPickerForNewImage = useCallback((layerId: string, columnId?: string, elementId?: string) => {
      handleOpenPhotoPicker((url, alt) => {
        if (columnId && elementId) {
          editor.updateElementById(layerId, columnId, elementId, { src: url, alt } as Partial<import('./dnd-model').Element>);
        } else {
          editor.updateLayerById(layerId, { src: url, alt } as Partial<Layer>);
        }
      });
    }, [editor, handleOpenPhotoPicker]);

    // Columns and social blocks are configured in a modal before they land:
    // the insert happens in the modal's confirm callback, not on drop/click.
    const addColumnsLayerViaModal = useCallback((index: number) => {
      handleOpenColumnsModal((layout) => {
        editor.addLayerInstance(createColumnsLayer({ layout }), index);
      });
    }, [editor, handleOpenColumnsModal]);

    const addSocialLayerViaModal = useCallback((index: number) => {
      const fresh = createSocialLayer();
      const previewBg = fresh.bgColor || editor.model.wrapper.bgColor || editor.model.globalStyles.contentBgColor || '#ffffff';
      handleOpenSocialModal(fresh.links, fresh.logoSetId ?? null, previewBg, ({ links, logoSetId, logos }) => {
        editor.addLayerInstance({ ...fresh, links, logoSetId, logos }, index);
      }, 'Add Social Links');
    }, [editor, handleOpenSocialModal]);

    const addSocialElementViaModal = useCallback((layerId: string, columnId: string, index?: number) => {
      const fresh = createSocialElement();
      const previewBg = editor.model.wrapper.bgColor || editor.model.globalStyles.contentBgColor || '#ffffff';
      handleOpenSocialModal(fresh.links, fresh.logoSetId ?? null, previewBg, ({ links, logoSetId, logos }) => {
        editor.addElementInstance(layerId, columnId, { ...fresh, links, logoSetId, logos }, index);
      }, 'Add Social Links');
    }, [editor, handleOpenSocialModal]);

    const handleCanvasDrop = useCallback(() => {
      if (draggedBlockId !== null) {
        const block = getBlockById(draggedBlockId);
        if (block) {
          // Dropping the Components tile picks the position now and the content
          // in the picker — nothing lands until the user confirms there.
          if (block.opensComponentPicker) {
            openComponentPicker(dropTargetIndex ?? editor.model.layers.length);
            setDraggedBlockId(null);
            setDraggedBlockTier(null);
            setDropTargetIndex(null);
            setColumnDropTarget(null);
            return;
          }
          if (block.savedRef) {
            const idx = dropTargetIndex ?? editor.model.layers.length;
            insertSavedBlock(block.savedRef, idx);
            setDraggedBlockId(null);
            setDraggedBlockTier(null);
            setDropTargetIndex(null);
            setColumnDropTarget(null);
            return;
          }
          const canBeElement = block.tier === 'element' || block.tier === 'both';
          if (canBeElement && columnDropTarget) {
            if (block.type === 'social') {
              // Configure the links in a modal before the element lands.
              addSocialElementViaModal(columnDropTarget.layerId, columnDropTarget.columnId);
            } else {
              // Use the id returned by the add — reading editor.selection here is
              // stale (selection state updates asynchronously).
              const elementId = editor.addElementByType(
                columnDropTarget.layerId,
                columnDropTarget.columnId,
                block.type as ElementType,
              );
              if (block.type === 'image') {
                openPickerForNewImage(columnDropTarget.layerId, columnDropTarget.columnId, elementId);
              }
            }
          } else {
            const idx = dropTargetIndex ?? editor.model.layers.length;
            if (block.type === 'columns') {
              addColumnsLayerViaModal(idx);
            } else if (block.type === 'social') {
              addSocialLayerViaModal(idx);
            } else {
              const layerId = editor.addLayerByType(block.type as LayerType, idx);
              if (block.type === 'image') {
                openPickerForNewImage(layerId);
              }
            }
          }
        }
      }
      setDraggedBlockId(null);
      setDraggedBlockTier(null);
      setDropTargetIndex(null);
      setColumnDropTarget(null);
    }, [draggedBlockId, dropTargetIndex, columnDropTarget, editor, openPickerForNewImage, insertSavedBlock, addColumnsLayerViaModal, addSocialLayerViaModal, addSocialElementViaModal, openComponentPicker]);

    // ---- Click-to-add from palette ----

    const handleClickToAdd = useCallback(
      (blockId: string, tier: BlockTier) => {
        const block = getBlockById(blockId);
        if (!block) return;

        // Clicking the Components tile opens the picker; a component has no
        // natural home, so it lands after the current selection.
        if (block.opensComponentPicker) {
          openComponentPicker(
            editor.selection.layerId
              ? editor.model.layers.findIndex((l) => l.id === editor.selection.layerId) + 1
              : editor.model.layers.length,
          );
          return;
        }

        if (block.savedRef) {
          // Clicking (not dragging) a saved block snaps it to where it belongs:
          // header → very top of the email, footer → very bottom.
          const insertIndex = block.savedRef === 'header' ? 0 : editor.model.layers.length;
          insertSavedBlock(block.savedRef, insertIndex);
          return;
        }

        const canBeElement = tier === 'element' || tier === 'both';
        if (canBeElement && selectedColumnsLayer) {
          const firstCol = selectedColumnsLayer.columns[0];
          if (firstCol) {
            if (block.type === 'social') {
              addSocialElementViaModal(selectedColumnsLayer.id, firstCol.id);
              return;
            }
            const elementId = editor.addElementByType(selectedColumnsLayer.id, firstCol.id, block.type as ElementType);
            if (block.type === 'image') {
              openPickerForNewImage(selectedColumnsLayer.id, firstCol.id, elementId);
            }
            return;
          }
        }

        const canBeLayer = tier === 'layer' || tier === 'both';
        if (!canBeLayer) return;

        const insertIndex = editor.selection.layerId
          ? editor.model.layers.findIndex((l) => l.id === editor.selection.layerId) + 1
          : editor.model.layers.length;
        if (block.type === 'columns') {
          addColumnsLayerViaModal(insertIndex);
          return;
        }
        if (block.type === 'social') {
          addSocialLayerViaModal(insertIndex);
          return;
        }
        const layerId = editor.addLayerByType(block.type as LayerType, insertIndex);
        if (block.type === 'image') {
          openPickerForNewImage(layerId);
        }
      },
      [editor, selectedColumnsLayer, openPickerForNewImage, insertSavedBlock, addColumnsLayerViaModal, addSocialLayerViaModal, addSocialElementViaModal, openComponentPicker],
    );

    // ---- Add a starter block to an empty column ----

    const handleAddBlockToColumn = useCallback((layerId: string, columnId: string, type: ElementType) => {
      if (type === 'social') {
        addSocialElementViaModal(layerId, columnId);
        return;
      }
      const elementId = editor.addElementByType(layerId, columnId, type);
      // Adding an image → open the picker immediately for the new element.
      if (type === 'image') {
        openPickerForNewImage(layerId, columnId, elementId);
      }
    }, [editor, openPickerForNewImage, addSocialElementViaModal]);

    // ---- Device change handler ----

    const handleDeviceChange = useCallback((newDevice: 'desktop' | 'mobile' | 'plaintext') => {
      if (newDevice === 'plaintext' && device !== 'plaintext') {
        setPlainTextContent(compileToPlainText(editor.model));
      }
      setDevice(newDevice);
    }, [device, editor.model]);

    // ---- Generate plain text from HTML ----

    const handleGeneratePlainText = useCallback(() => {
      setPlainTextContent(compileToPlainText(editor.model));
    }, [editor.model]);

    // ---- Preview ----

    const getHtmlForPreview = useCallback(() => {
      return compileModel(editor.model);
    }, [editor.model]);

    // ---- Keyboard shortcuts ----

    useEffect(() => {
      const handler = (e: KeyboardEvent) => {
        const isCmd = e.metaKey || e.ctrlKey;
        // While editing text inline, let the browser own undo/redo/duplicate so
        // we don't clobber the caret or the in-progress edit.
        const inEditable = !!(e.target as HTMLElement)?.isContentEditable;

        if (isCmd && e.key === 'z' && !e.shiftKey) {
          if (inEditable) return;
          e.preventDefault();
          editor.undo();
        } else if ((isCmd && e.key === 'y') || (isCmd && e.shiftKey && e.key === 'z')) {
          if (inEditable) return;
          e.preventDefault();
          editor.redo();
        } else if (isCmd && e.key === 'd') {
          if (inEditable) return;
          e.preventDefault();
          const { layerId, columnId, elementId } = editor.selection;
          if (elementId && layerId && columnId) editor.duplicateElementById(layerId, columnId, elementId);
          else if (layerId) editor.duplicateLayerById(layerId);
        } else if (isCmd && e.key === 's') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          const target = e.target as HTMLElement;
          if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
          const { layerId, columnId, elementId } = editor.selection;
          if (layerId && columnId && elementId) {
            const layer = editor.model.layers.find((l) => l.id === layerId);
            if (layer?.type === 'columns') {
              const col = (layer as ColumnsLayer).columns.find((c) => c.id === columnId);
              const idx = col ? col.elements.findIndex((el) => el.id === elementId) : -1;
              const to = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
              if (col && idx !== -1 && to >= 0 && to < col.elements.length) {
                e.preventDefault();
                editor.moveElementByIndex(layerId, columnId, idx, columnId, to);
              }
            }
          }
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          const target = e.target as HTMLElement;
          if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
          if (editor.selection.elementId && editor.selection.layerId && editor.selection.columnId) {
            editor.removeElementById(editor.selection.layerId, editor.selection.columnId, editor.selection.elementId);
          } else if (editor.selection.layerId) {
            editor.removeLayerById(editor.selection.layerId);
          }
        } else if (e.key === 'Escape') {
          editor.clearSelection();
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [editor]);

    // ---- Save handler ----

    const handleSave = useCallback(() => {
      const html = compileModel(editor.model);
      const plain = plainTextContent || compileToPlainText(editor.model);
      editor.markSaved();
      onSave?.(editor.model, html, plain);
    }, [editor, onSave, plainTextContent]);

    // Same compile-and-mark-clean path as handleSave; only the host's callback
    // differs, which is what decides whether the API keeps the old version row.
    const handleSaveAsNewVersion = useCallback(() => {
      if (!onSaveAsNewVersion) return;
      const html = compileModel(editor.model);
      const plain = plainTextContent || compileToPlainText(editor.model);
      editor.markSaved();
      onSaveAsNewVersion(editor.model, html, plain);
    }, [editor, onSaveAsNewVersion, plainTextContent]);

    // ---- Template apply ----
    // Replaces the whole model (undoable via editor.setModel), so the picker's
    // own "this will replace your current content" confirm is the safety net.

    const handleTemplateSelect = useCallback((template: unknown) => {
      const tmpl = template as FtpTemplateRow;
      const model = modelFromTemplate(tmpl);
      if (!model) {
        // The picker only lists templates isDndTemplate() accepted, so this means
        // the design itself failed to parse rather than the user picking an HTML one.
        toast.error("This template's design couldn't be loaded.");
        return;
      }
      editor.setModel(model);
      setPlainTextContent(tmpl.plain_content || compileToPlainText(model));
      onTemplateApply?.(tmpl);
    }, [editor, onTemplateApply, toast]);

    // ---- Convert to HTML ----

    const handleConvertToHtml = useCallback(() => {
      const html = compileModel(editor.model);
      onConvertToHtml?.(html);
    }, [editor.model, onConvertToHtml]);

    // ---- Render ----

    // Preview layout = previewMode and not currently expanded to fullscreen.
    const previewOnly = previewMode && !isFullscreen;

    const editorContent = (
      <div className={`dnd-editor${isFullscreen ? ' dnd-fullscreen' : ''}${previewOnly ? ' dnd-preview-mode' : ''}`}>
        {previewOnly ? (
          <div className="dnd-preview-toolbar">
            <div className="dnd-preview-toolbar-left">
              <button className="dnd-toolbar-btn" onClick={editor.undo} disabled={!editor.canUndo} title="Undo"><Undo2 size={14} /></button>
              <button className="dnd-toolbar-btn" onClick={editor.redo} disabled={!editor.canRedo} title="Redo"><Redo2 size={14} /></button>
              <button className="dnd-toolbar-btn" onClick={editor.revert} disabled={!editor.canRevert} title="Revert to saved"><History size={14} /></button>
            </div>
            <div className="dnd-preview-toolbar-center">
              <button className={`dnd-toolbar-btn${device === 'desktop' ? ' active' : ''}`} onClick={() => handleDeviceChange('desktop')} title="Desktop preview"><Monitor size={14} /></button>
              <button className={`dnd-toolbar-btn${device === 'mobile' ? ' active' : ''}`} onClick={() => handleDeviceChange('mobile')} title="Mobile preview"><Smartphone size={14} /></button>
              <button className={`dnd-toolbar-btn${device === 'plaintext' ? ' active' : ''}`} onClick={() => handleDeviceChange('plaintext')} title="Plain text"><FileText size={14} /></button>
            </div>
            <div className="dnd-preview-toolbar-right">
              {enableTemplatePicker && (
                <button className="dnd-toolbar-btn" onClick={() => setTemplatePickerOpen(true)} title="Choose template" aria-label="Templates"><LayoutTemplate size={14} /></button>
              )}
              <button className="dnd-toolbar-btn" onClick={() => setPreviewOpen(true)} title="Preview"><Eye size={14} /></button>
              <button className="dnd-preview-edit-btn" onClick={() => setIsFullscreen(true)} title="Open full editor">
                <PanelLeftOpen size={14} />
                Open Editor
              </button>
            </div>
          </div>
        ) : (
          <DndToolbar
            canUndo={editor.canUndo}
            canRedo={editor.canRedo}
            onUndo={editor.undo}
            onRedo={editor.redo}
            canRevert={editor.canRevert}
            onRevert={editor.revert}
            onOpenTemplatePicker={enableTemplatePicker ? () => setTemplatePickerOpen(true) : undefined}
            device={device}
            onDeviceChange={handleDeviceChange}
            onPreview={() => setPreviewOpen(true)}
            onSave={handleSave}
            onSaveAsNewVersion={onSaveAsNewVersion ? handleSaveAsNewVersion : undefined}
            onConvertToHtml={onConvertToHtml ? handleConvertToHtml : undefined}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => {
              if (isFullscreen && onClose) onClose();
              else setIsFullscreen((f) => !f);
            }}
            isDirty={editor.isDirty}
            hideSave={hideSave}
          />
        )}

        <div className="dnd-editor-body">
          {!previewOnly && !isPlainText && (
            <div className="dnd-left-panel">
              <DndBlockPalette
                onDragStart={handlePaletteDragStart}
                onDragEnd={handleDragEnd}
                onClick={handleClickToAdd}
                savedBlocks={savedEntries}
              />
            </div>
          )}

          {isPlainText ? (
            <div className="dnd-plain-text-pane">
              <textarea
                className="dnd-plain-text-editor"
                value={plainTextContent}
                onChange={(e) => setPlainTextContent(e.target.value)}
                placeholder="Plain text version of your email..."
                spellCheck={false}
              />
            </div>
          ) : (
            <DndCanvas
              layers={editor.model.layers}
              globalStyles={editor.model.globalStyles}
              wrapper={editor.model.wrapper}
              selection={editor.selection}
              device={device}
              onSelectLayer={editor.selectLayer}
              onSelectElement={editor.selectElement}
              onSelectColumn={editor.selectColumn}
              onRemoveLayer={editor.removeLayerById}
              onDuplicateLayer={editor.duplicateLayerById}
              onUpdateLayer={editor.updateLayerById}
              onUpdateElement={editor.updateElementById}
              onRemoveElement={editor.removeElementById}
              onDuplicateElement={editor.duplicateElementById}
              onAddBlockToColumn={handleAddBlockToColumn}
              onOpenPhotoPicker={handleOpenPhotoPicker}
              onOpenLinkModal={handleOpenLinkModal}
              onOpenVariableModal={handleOpenVariableModal}
              onOpenHtmlModal={handleOpenHtmlModal}
              onOpenSocialModal={handleOpenSocialModal}
              onOpenColumnsModal={handleOpenColumnsModalForEdit}
              editRequest={editRequest}
              onEditRequestHandled={handleEditRequestHandled}
              isDragging={isDragging}
              draggedBlockTier={draggedBlockTier}
              dropTargetIndex={dropTargetIndex}
              onDropIndexChange={setDropTargetIndex}
              columnDropTarget={columnDropTarget}
              onColumnDropTargetChange={setColumnDropTarget}
              onCanvasDrop={handleCanvasDrop}
              isReordering={isReordering}
              reorderFromIndex={reorderFromIndex}
              onReorderStart={handleReorderStart}
              onReorderEnd={handleReorderEnd}
              onReorderDrop={handleReorderDrop}
              isElementDragging={isElementDragging}
              elementDropTarget={elementDropTarget}
              onElementDragStart={handleElementDragStart}
              onElementDropTargetChange={setElementDropTarget}
              onElementDrop={handleElementDrop}
              onElementDragOut={handleElementDragOut}
              onElementDragEnd={handleElementDragEnd}
            />
          )}

          {!previewOnly && (isPlainText ? (
            <div className="dnd-right-panel">
              <div className="dnd-right-tabs">
                <button className="active" style={{ pointerEvents: 'none' }}>Plain Text</button>
              </div>
              <div className="dnd-inspector-scroll" style={{ padding: 16 }}>
                <button
                  className="dnd-toolbar-btn"
                  style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
                  onClick={handleGeneratePlainText}
                >
                  Generate from HTML
                </button>
                <p style={{ fontSize: 12, color: '#6c7086', lineHeight: 1.5, margin: 0 }}>
                  Edit the plain text version of your email. This is shown to recipients
                  whose email client doesn't support HTML.
                </p>
              </div>
            </div>
          ) : (
            <div className="dnd-right-panel">
              <div className="dnd-right-tabs">
                <button className={rightTab === 'inspector' ? 'active' : ''} onClick={() => setRightTab('inspector')}>Inspector</button>
                <button className={rightTab === 'global' ? 'active' : ''} onClick={() => setRightTab('global')}>Design</button>
              </div>
              <ColorPresetsContext.Provider value={colorPresets}>
                {rightTab === 'inspector' ? (
                  <DndInspector
                    selection={editor.selection}
                    model={editor.model}
                    onUpdateLayer={editor.updateLayerById}
                    onUpdateElement={editor.updateElementById}
                    onUpdateColumn={editor.updateColumnById}
                    onSelectLayer={editor.selectLayer}
                    onSelectColumn={editor.selectColumn}
                    onSelectElement={editor.selectElement}
                    onOpenPhotoPicker={handleOpenPhotoPicker}
                    onOpenHtmlModal={handleOpenHtmlModal}
                    onRequestInlineEdit={requestInlineEdit}
                  />
                ) : (
                  <DndGlobalStyles
                    globalStyles={editor.model.globalStyles}
                    wrapper={editor.model.wrapper}
                    preheaderText={editor.model.preheaderText}
                    onUpdateGlobalStyles={editor.setGlobalStyles}
                    onUpdateWrapper={editor.setWrapper}
                    onUpdatePreheader={editor.setPreheaderText}
                  />
                )}
              </ColorPresetsContext.Provider>
            </div>
          ))}
        </div>

        <FtpPhotoPicker
          isOpen={photoPickerOpen}
          onClose={() => { setPhotoPickerOpen(false); photoPickerCallback.current = null; }}
          onSelect={handlePhotoSelect}
        />

        {/* No onOptimize: optimizing rewrites raw HTML, which this editor
            regenerates from its model on every compile. */}
        <FtpPreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          getHtml={getHtmlForPreview}
        />

        <FtpLinkModal
          isOpen={linkModalOpen}
          onClose={() => { setLinkModalOpen(false); linkCallback.current = null; }}
          onInsert={handleLinkInsert}
          hasSelection={linkModalHasSelection}
        />

        <FtpVariableInserter
          isOpen={variableModalOpen}
          onClose={() => { setVariableModalOpen(false); variableCallback.current = null; }}
          onSelect={handleVariableSelect}
          variables={variables}
        />

        <DndHtmlCodeModal
          isOpen={htmlModalOpen}
          initialHtml={htmlModalInitial}
          variables={variables}
          onSave={handleHtmlModalSave}
          onClose={() => { setHtmlModalOpen(false); htmlModalCallback.current = null; }}
        />

        <DndColumnsModal
          isOpen={columnsModalOpen}
          initialLayout={columnsModalInitialLayout}
          confirmLabel={columnsModalConfirmLabel}
          onConfirm={handleColumnsModalConfirm}
          onClose={() => { setColumnsModalOpen(false); columnsModalCallback.current = null; }}
        />

        <DndComponentPicker
          isOpen={componentPickerOpen}
          components={componentEntries}
          onClose={() => {
            setComponentPickerOpen(false);
            pendingComponentIndex.current = undefined;
          }}
          onInsert={handleComponentPicked}
        />

        <FtpTemplatePicker
          isOpen={templatePickerOpen}
          onClose={() => setTemplatePickerOpen(false)}
          onApply={() => {}}
          mode="select"
          onSelect={handleTemplateSelect}
          filter={isDndTemplate}
          emptyNote="No drag & drop templates found. HTML templates can't be loaded into this editor."
        />

        <DndSocialModal
          isOpen={socialModalOpen}
          initialLinks={socialModalLinks}
          initialLogoSetId={socialModalLogoSetId}
          previewBg={socialModalPreviewBg}
          confirmLabel={socialModalConfirmLabel}
          onSave={handleSocialModalSave}
          onClose={() => { setSocialModalOpen(false); socialModalCallback.current = null; }}
        />
      </div>
    );

    // A drag-and-drop canvas has no narrow-viewport story. Gated here as well as in the
    // email host, because the marketing settings page imports this component directly and
    // so is not covered by that gate.
    const tooNarrow = useDesktopGate();
    if (tooNarrow) return <DesktopOnlyNotice what="The drag-and-drop editor" />;

    // Portal to <body> (like FtpEditor) so fullscreen escapes any nested stacking
    // context and reliably covers page chrome (e.g. the recent-patients chevron).
    if (isFullscreen) {
      return createPortal(<div className="dnd-fullscreen-portal">{editorContent}</div>, document.body);
    }

    return editorContent;
  },
);

export default DndEditor;
